import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { ExternalProviderService, ToastService } from '@raptor-fe/services';
import { firstValueFrom } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { ExternalProvider } from '@raptor-fe/core';

export type ExternalProviderScope = 'tenant' | 'branch' | 'user';

export interface ExternalProviderState {
    providers: ExternalProvider[];
    inheritedTenantProviders: ExternalProvider[];
    inheritedBranchProviders: ExternalProvider[];
    allParentProviders: ExternalProvider[];
    loading: boolean;
}

export const ExternalProviderStore = signalStore(
    withState<ExternalProviderState>({
        providers: [],
        inheritedTenantProviders: [],
        inheritedBranchProviders: [],
        allParentProviders: [],
        loading: false,
    }),
    withMethods((store, service = inject(ExternalProviderService), toastService = inject(ToastService)) => ({
        async fetchByTenantId(tenantId: string): Promise<void> {
            patchState(store, { loading: true });

            const providers = await firstValueFrom(service.getByTenantId(tenantId));

            patchState(store, {
                providers,
                inheritedTenantProviders: [],
                inheritedBranchProviders: [],
                allParentProviders: [],
                loading: false,
            });
        },

        async fetchByBranchId(tenantId: string, branchId: string): Promise<void> {
            patchState(store, { loading: true });

            const [branchProviders, tenantProviders] = await Promise.all([
                firstValueFrom(service.getByBranchId(tenantId, branchId)),
                firstValueFrom(service.getByTenantId(tenantId)),
            ]);

            const overriddenTypes = branchProviders.map((bp) => bp.type);
            const inheritedTenant = tenantProviders.filter((tp) => !overriddenTypes.includes(tp.type));

            patchState(store, {
                providers: branchProviders,
                inheritedTenantProviders: inheritedTenant,
                inheritedBranchProviders: [],
                allParentProviders: tenantProviders,
                loading: false,
            });
        },

        async fetchByUserBranch(tenantId: string, branchId: string, userId: string): Promise<void> {
            patchState(store, { loading: true });

            const [userProviders, branchProviders, tenantProviders] = await Promise.all([
                firstValueFrom(service.getByUserBranch(tenantId, branchId, userId)),
                firstValueFrom(service.getByBranchId(tenantId, branchId)),
                firstValueFrom(service.getByTenantId(tenantId)),
            ]);

            const userOverriddenTypes = userProviders.map((up) => up.type);
            const inheritedBranch = branchProviders.filter((bp) => !userOverriddenTypes.includes(bp.type));
            const allOverriddenTypes = [...userOverriddenTypes, ...inheritedBranch.map((bp) => bp.type)];
            const inheritedTenant = tenantProviders.filter((tp) => !allOverriddenTypes.includes(tp.type));

            patchState(store, {
                providers: userProviders,
                inheritedTenantProviders: inheritedTenant,
                inheritedBranchProviders: inheritedBranch,
                allParentProviders: [...tenantProviders, ...branchProviders],
                loading: false,
            });
        },

        async upsert(
            tenantId: string,
            provider: ExternalProvider,
            isNew: boolean,
            scope: ExternalProviderScope,
            branchId?: string,
            userId?: string,
        ): Promise<void> {
            patchState(store, { loading: true });

            let save$;
            if (scope === 'tenant') {
                save$ = service.save(tenantId, provider, isNew);
            } else if (scope === 'branch') {
                save$ = service.saveBranch(tenantId, branchId as string, provider, isNew);
            } else {
                save$ = service.saveUserBranch(tenantId, branchId as string, userId as string, provider, isNew);
            }

            await firstValueFrom(
                save$.pipe(
                    tapResponse({
                        next: async () => {
                            toastService.showSuccess(`External provider ${isNew ? 'created' : 'updated'} successfully.`);
                            await this.refetch(tenantId, scope, branchId, userId);
                        },
                        error: (error: { error: Error }) => {
                            patchState(store, { loading: false });
                            toastService.showError(error?.error?.message ?? 'Failed to save external provider.');
                        },
                    }),
                ),
            );
        },

        async override(
            tenantId: string,
            parentProvider: ExternalProvider,
            scope: ExternalProviderScope,
            branchId?: string,
            userId?: string,
        ): Promise<void> {
            const scopedProvider: ExternalProvider = {
                type: parentProvider.type,
                apiId: parentProvider.apiId,
                apiKey: parentProvider.apiKey,
                tenantId,
                branchId,
                userId,
            };

            patchState(store, { loading: true });

            let save$;
            if (scope === 'branch') {
                save$ = service.saveBranch(tenantId, branchId as string, scopedProvider, true);
            } else {
                save$ = service.saveUserBranch(tenantId, branchId as string, userId as string, scopedProvider, true);
            }

            await firstValueFrom(
                save$.pipe(
                    tapResponse({
                        next: async () => {
                            toastService.showSuccess('Parent provider overridden successfully.');
                            await this.refetch(tenantId, scope, branchId, userId);
                        },
                        error: (error: { error: Error }) => {
                            patchState(store, { loading: false });
                            toastService.showError(error?.error?.message ?? 'Failed to override provider.');
                        },
                    }),
                ),
            );
        },

        async delete(tenantId: string, id: string, scope: ExternalProviderScope, branchId?: string, userId?: string): Promise<void> {
            patchState(store, { loading: true });

            let delete$;
            if (scope === 'tenant') {
                delete$ = service.delete(tenantId, id);
            } else if (scope === 'branch') {
                delete$ = service.deleteBranch(tenantId, branchId as string, id);
            } else {
                delete$ = service.deleteUserBranch(tenantId, branchId as string, userId as string, id);
            }

            await firstValueFrom(
                delete$.pipe(
                    tapResponse({
                        next: async () => {
                            toastService.showSuccess('External provider removed successfully.');
                            await this.refetch(tenantId, scope, branchId, userId);
                        },
                        error: (error: { error: Error }) => {
                            patchState(store, { loading: false });
                            toastService.showError(error?.error?.message ?? 'Failed to delete external provider.');
                        },
                    }),
                ),
            );
        },

        async refetch(tenantId: string, scope: ExternalProviderScope, branchId?: string, userId?: string): Promise<void> {
            if (scope === 'tenant') {
                await this.fetchByTenantId(tenantId);
            } else if (scope === 'branch') {
                await this.fetchByBranchId(tenantId, branchId as string);
            } else {
                await this.fetchByUserBranch(tenantId, branchId as string, userId as string);
            }
        },

        clear(): void {
            patchState(store, {
                providers: [],
                inheritedTenantProviders: [],
                inheritedBranchProviders: [],
                allParentProviders: [],
                loading: false,
            });
        },
    })),
);
